import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { fetchActiveQuote, fetchFreshStrejf, fetchFeaturedRecipeOfWeek, fetchCurrentVignette, fetchVideos } from "@/lib/queries";
import { fetchSubscriberCount } from "@/lib/subscribers.functions";
import { type Topic } from "@/lib/topics";
import { useReadArticles } from "@/hooks/use-read-articles";
import { useTopicPreferences } from "@/hooks/use-topic-preferences";
import { usePaywallFilter } from "@/hooks/use-paywall-filter";
import { SubscribeSection } from "@/components/SubscribeSection";
import { detectFormat } from "@/lib/format-detector";
import { currentGaze, currentReading } from "@/lib/weekly";

import { WikiName, renderWithWikiLinks } from "@/lib/wiki-link";
import { ListenButton } from "@/components/ListenButton";
import { voiceForAuthor } from "@/lib/voice-gender";
import { HomeManifest } from "@/components/HomeManifest";
import { HowToRead } from "@/components/HowToRead";
import { WeeklyFund } from "@/components/WeeklyFund";
import { StrejfList } from "@/components/StrejfList";
import { LatestMediaSection } from "@/components/LatestMediaSection";
import { SupportNote } from "@/components/SupportNote";
import { OutboundArrow } from "@/components/OutboundArrow";
import cahierMotif from "@/assets/motifs/boger.png";
import brevMotif from "@/assets/motifs/poesi.png";

// Fjerner markdown-links [tekst](url) og overflødige tegn før oplæsning,
// så stemmen ikke læser URL'er højt.
function toSpokenText(input: string): string {
  return input
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/[*_`>#]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// Kort førstelinjeuddrag til manchet på forsiden.
// Scrapet essay-tekst starter ofte med site-chrome ("Subscribe", "Sign in",
// "Discover more from ...", "Photo by ...", "By subscribing you agree ..."),
// som vi filtrerer væk indtil vi rammer første rigtige prosalinje.
function firstParagraph(input: string | null | undefined, max = 320): string | null {
  if (!input) return null;

  // Strip markdown-links [tekst](url) og billeder først.
  const stripped = input
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");

  const junkLine = (l: string) => {
    const t = l.trim();
    if (!t) return true;
    if (/^(subscribe|sign ?in|sign ?up|log ?in|share this|discover more from|by subscribing|already have an account|photo(graph)? by|image by|illustration by|photo credit|photograph:|©|copyright|follow us|newsletter|read more|continue reading|advertisement|skip to|menu|search|home)\b/i.test(t)) return true;
    // Audio-narration og contributor-bio-blokke i toppen af Emergence/Aeon m.fl.
    if (/^(listen to (this |the )?(story|essay|piece|article|audio)|narrated by|read by|voiced by|audio version|contributor bios?|about the (author|contributor|writer)|author bio)\b/i.test(t)) return true;
    if (/\b(is the author of|essays? have appeared in|work has appeared in|writing has appeared in|is a writer based in|is a journalist based in|is a poet based in|is an essayist)\b/i.test(t)) return true;
    if (t.length < 40) return true; // korte chrome-fragmenter
    // "Vittles is an online magazine ..." → boilerplate om udgiveren
    if (/\b(is an online magazine|is a magazine|is a newsletter|is a publication|terms of use|privacy policy|information collection)\b/i.test(t)) return true;
    return false;
  };

  const lines = stripped.split(/\n+/);
  let i = 0;
  while (i < lines.length && junkLine(lines[i])) i++;
  const rest = lines.slice(i).join(" ");
  const cleaned = rest.replace(/\s+/g, " ").trim();
  if (!cleaned) return null;
  if (cleaned.length <= max) return cleaned;
  const cut = cleaned.slice(0, max);
  const lastStop = Math.max(cut.lastIndexOf(". "), cut.lastIndexOf("? "), cut.lastIndexOf("! "));
  return (lastStop > 120 ? cut.slice(0, lastStop + 1) : cut).trim() + " …";
}

const SITE_URL = "https://flaneriet.online";
const HOME_OG_IMAGE = `${SITE_URL}/og/og-forsiden.png`;


export const Route = createFileRoute("/")({
  loader: async ({ context }) => {
    const [quote, digest, featuredRecipe, vignette, subscriberCount, latestVideo] = await Promise.all([
      context.queryClient.ensureQueryData({ queryKey: ["quote"], queryFn: fetchActiveQuote }),
      context.queryClient.ensureQueryData({ queryKey: ["fresh-strejf"], queryFn: fetchFreshStrejf }),
      context.queryClient.ensureQueryData({
        queryKey: ["featured-recipe"],
        queryFn: fetchFeaturedRecipeOfWeek,
        staleTime: 6 * 60 * 60 * 1000,
      }),
      context.queryClient.ensureQueryData({
        queryKey: ["current-vignette"],
        queryFn: fetchCurrentVignette,
        staleTime: 60 * 60 * 1000,
      }),
      context.queryClient.ensureQueryData({
        queryKey: ["subscriber-count"],
        queryFn: () => fetchSubscriberCount(),
        staleTime: 60 * 60 * 1000,
      }),
      context.queryClient.ensureQueryData({
        queryKey: ["latest-video"],
        queryFn: () => fetchVideos({ limit: 1 }),
        staleTime: 60 * 60 * 1000,
      }),
    ]);
    return { quote, digest, featuredRecipe, vignette, subscriberCount, latestVideo };

  },

  head: ({ loaderData }) => {
    const q = loaderData?.quote as { text?: string; author?: string | null } | null | undefined;
    const scripts = q?.text
      ? [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Quotation",
              text: q.text,
              creator: q.author ? { "@type": "Person", name: q.author } : undefined,
              inLanguage: "da-DK",
              url: `${SITE_URL}/`,
            }),
          },
        ]
      : [];
    return {
      meta: [
        { title: "Flâneriet – langsomt liv, dagligt kurateret" },
        { name: "description", content: "Dagens citat, ugens digt og friske strejf fra vin, kunst, jazz, poesi, litteratur, mad, cykling og levekunst – samlet ét sted." },
        { property: "og:type", content: "website" },
        { property: "og:site_name", content: "Flâneriet" },
        { property: "og:title", content: "Flâneriet – langsomt liv, dagligt kurateret" },
        { property: "og:description", content: "Dagens citat, ugens digt og friske strejf fra vin, kunst, jazz, poesi, litteratur, mad, cykling og levekunst." },
        { property: "og:url", content: `${SITE_URL}/` },
        { property: "og:image", content: HOME_OG_IMAGE },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: "Flâneriet – langsomt liv, dagligt kurateret" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: "Flâneriet – langsomt liv, dagligt kurateret" },
        { name: "twitter:description", content: "Dagens citat, ugens digt og friske strejf fra vin, kunst, jazz, poesi, litteratur, mad, cykling og levekunst." },
        { name: "twitter:image", content: HOME_OG_IMAGE },
      ],
      links: [{ rel: "canonical", href: `${SITE_URL}/` }],
      scripts,
    };
  },
  component: Index,
});



function isBookish(url: string, title: string) {
  const f = detectFormat(url, title);
  return f === "book" || f === "audiobook";
}

function Index() {
  const { markRead } = useReadArticles();
  const { enabled } = useTopicPreferences();
  const { hidePaywall } = usePaywallFilter();

  const quote = useQuery({ queryKey: ["quote"], queryFn: fetchActiveQuote });
  const digest = useQuery({ queryKey: ["fresh-strejf"], queryFn: fetchFreshStrejf });

  const digestList = useMemo(
    () => {
      const raw = ((digest.data ?? []) as any[])
        .filter((a: any) => !isBookish(a.original_url, a.title))
        .filter((a: any) => enabled.has(a.topic as Topic))
        .filter((a: any) => !hidePaywall || !a.paywall);

      const MAX_PER_TOPIC = 3;
      const MAX_TOTAL = 9;
      const MIN_TOTAL = 2;
      const byTopic: Record<string, number> = {};
      const dansk = raw.filter((a: any) => a.language === "da");
      const engelsk = raw.filter((a: any) => a.language !== "da");
      const ordered = [...dansk, ...engelsk];
      const out: any[] = [];
      for (const a of ordered) {
        if (out.length >= MAX_TOTAL) break;
        const n = byTopic[a.topic] ?? 0;
        if (n >= MAX_PER_TOPIC) continue;
        byTopic[a.topic] = n + 1;
        out.push(a);
      }
      // Aldrig 1 alene: enten 2-9 eller ingenting.
      if (out.length < MIN_TOTAL) return [];
      return out.sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    },
    [digest.data, enabled, hidePaywall]
  );

  const reading = currentReading();
  const gaze = currentGaze();

  const featuredRecipeQuery = useQuery({
    queryKey: ["featured-recipe"],
    queryFn: fetchFeaturedRecipeOfWeek,
    staleTime: 6 * 60 * 60 * 1000,
  });
  const featuredRecipe = featuredRecipeQuery.data ?? null;

  const subscriberCountQuery = useQuery({
    queryKey: ["subscriber-count"],
    queryFn: () => fetchSubscriberCount(),
    staleTime: 60 * 60 * 1000,
  });
  const subscriberCount = subscriberCountQuery.data ?? null;

  const vignetteQuery = useQuery({
    queryKey: ["current-vignette"],
    queryFn: fetchCurrentVignette,
    staleTime: 60 * 60 * 1000,
  });
  const vignette = vignetteQuery.data ?? null;

  const latestVideoQuery = useQuery({
    queryKey: ["latest-video"],
    queryFn: () => fetchVideos({ limit: 1 }),
    staleTime: 60 * 60 * 1000,
  });
  const latestVideo = latestVideoQuery.data?.[0] ?? null;

  // Ugens fund = søndagsbrevet. Kun hvis der er en vignette med kilde falder
  // vi tilbage på seneste fund fra friske strejf.
  const heroFromVignette = vignette && vignette.source_url && vignette.title
    ? {
        id: `vignette-${vignette.week_start}`,
        original_url: vignette.source_url,
        title: vignette.title,
        summary: firstParagraph(vignette.excerpt),
        selection_reason:
          vignette.why_read?.trim() ||
          (vignette.author
            ? `Et essay af ${vignette.author}, hentet fra en kurateret liste af redaktionelle udgivere jeg selv læser. Skrevet af et menneske – ikke en maskine – og valgt fordi det bærer den langsomme, opmærksomme tone som Flâneriet står for.`
            : "Et essay hentet fra en kurateret liste af redaktionelle udgivere jeg selv læser. Skrevet af et menneske – ikke en maskine – og valgt fordi det bærer den langsomme, opmærksomme tone som Flâneriet står for."),

        source: vignette.source_name ?? new URL(vignette.source_url).hostname.replace(/^www\./, ""),
        topic: "litteratur",
        paywall: false,
        image_url: null,
      }
    : null;

  return (
    <div>
      <h1 className="sr-only">Flâneriet – dagligt kurateret samlingspunkt for langsomt liv</h1>

      {/* Manifest + kurator-signatur */}
      <HomeManifest />

      {/* Sådan læses – rolig redaktionel note under manifestet */}
      <HowToRead />

      {/* Ugens fund = søndagsbrevet (hero) */}
      {heroFromVignette ? (
        <WeeklyFund
          article={heroFromVignette as any}
          eyebrow="Ugens søndagsbrev · essay"
          readLabel="Læs søndagsbrevet hos "
          reasonLabel="Om essayet"
        />
      ) : digestList.length > 0 ? (
        <WeeklyFund
          article={digestList[0] as any}
          onOpen={() => markRead(digestList[0].id)}
        />
      ) : null}


      {/* Dagens ord | Ugens læsestund */}
      <section className="rule-double grid grid-cols-1 md:grid-cols-2">
        {/* Dagens ord – mørk boks */}
        {quote.data ? (
          <div className="bg-ink text-paper p-8 md:p-12 flex flex-col justify-start">
            <p className="font-serif text-[11px] font-medium tracking-[0.22em] uppercase text-accent-onDark mb-4">
              Dagens ord
            </p>
            <div className="font-serif italic text-[22px] md:text-[27px] leading-[1.36]">
              {quote.data.text}
            </div>
            <div className="mt-6 flex items-center gap-5">
              {quote.data.author && (
                <p className="font-serif text-[11px] tracking-[0.1em] uppercase text-[#b7ad9a] m-0">
                  <WikiName name={quote.data.author} />
                </p>
              )}
              <ListenButton
                variant="onDark"
                text={toSpokenText(`Dagens ord. ${quote.data.text}${quote.data.author ? ". " + quote.data.author : ""}`)}
                voice={voiceForAuthor(quote.data.author)}
              />
            </div>
          </div>
        ) : (
          <div className="bg-ink text-paper p-8 md:p-12" />
        )}

        {/* Dagens digt */}
        <div className="p-8 md:p-12 flex flex-col justify-start md:border-l md:border-rule">
          <p className="eyebrow mb-4">Dagens digt</p>
          <h3 className="font-serif text-[24px] md:text-[26px] leading-[1.14] m-0 mb-2 text-ink">
            {reading.title}
          </h3>
          <p className="font-serif italic text-[14px] text-muted mb-3.5">
            <WikiName name={reading.author} />{reading.year ? ` · ${reading.year}` : ""} · {reading.form}
          </p>
          <p className="font-serif italic font-light text-[15px] md:text-[17px] leading-[1.55] text-ink-body m-0">
            {reading.text.split("\n\n")[0]}
          </p>
          {reading.source && (
            <p className="font-serif text-[12.5px] leading-[1.5] text-muted mt-3.5 m-0">
              {reading.source}
            </p>
          )}
          <div className="mt-5">
            <ListenButton
              text={toSpokenText(`Dagens digt. ${reading.title}, af ${reading.author}. ${reading.text}`)}
              voice={voiceForAuthor(reading.author)}
            />
          </div>
        </div>
      </section>

      {/* Ugens strejf – kompakt liste med thumbs, gem + outbound.
          Hvis søndagsbrevet er hero, viser vi alle friske strejf; ellers
          slice(1) fordi digestList[0] allerede er hero. */}
      {(() => {
        const items = heroFromVignette ? digestList : digestList.slice(1);
        if (items.length === 0) return null;
        return (
          <StrejfList
            items={items as any}
            onOpen={(id) => markRead(id)}
          />
        );
      })()}

      {/* Ugens blik – fast plads (ingen rotation) */}
      <section className="rule-double py-14">
        <p className="eyebrow text-center mb-7">Ugens blik</p>

        <div className="grid grid-cols-1 md:grid-cols-[1.2fr_1fr] gap-10 md:gap-12 items-center">
          <a
            href={gaze.wikiUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block overflow-hidden bg-muted aspect-[4/3] border border-rule"
            aria-label={`Læs om ${gaze.title} af ${gaze.artist} på Wikipedia`}
          >
            <img
              src={gaze.imageUrl}
              alt={`${gaze.title} af ${gaze.artist}`}
              className="w-full h-full object-cover transition-transform duration-[1200ms] hover:scale-[1.01]"
              loading="lazy"
            />
          </a>
          <div>
            <h2 className="font-serif text-[32px] md:text-[40px] leading-[1.06] m-0 mb-2 text-ink">
              {gaze.title}
            </h2>
            <p className="font-serif italic text-[14px] text-muted mb-4">
              <a
                href={gaze.wikiUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-accent transition-colors"
              >
                {gaze.artist}
              </a>
              {gaze.year ? ` · ${gaze.year}` : ""}
              {gaze.medium ? ` · ${gaze.medium}` : ""}
            </p>
            <p className="font-serif font-light text-[16px] md:text-[17px] leading-[1.7] text-ink-body m-0">
              {renderWithWikiLinks(gaze.note)}
            </p>
          </div>
        </div>
      </section>

      <LatestMediaSection recipe={featuredRecipe} video={latestVideo} />

      {/* Cahier | Nyhedsbrev */}
      <section className="rule-double grid grid-cols-1 md:grid-cols-2 items-start">
        <div className="py-8 md:py-12 md:pr-11 md:border-r md:border-rule">
          <img
            src={cahierMotif}
            alt=""
            aria-hidden="true"
            width={512}
            height={512}
            loading="lazy"
            className="mb-5 w-[72px] md:w-[88px] h-auto opacity-80"
          />
          <p className="eyebrow mb-4">Søndagens samling · på papir</p>
          <h3 className="font-serif text-[28px] md:text-[34px] leading-[1.08] m-0 mb-3.5 text-ink">
            Et lille cahier at holde i hånden
          </h3>
          <p className="font-serif font-light text-[15px] md:text-[16px] leading-[1.72] text-ink-body mb-6 max-w-md">
            Ugens brev, dagens ord, opskriften og et par strejf – samlet til A4, du kan printe søndag morgen.
            Læs det langsomt. Og giv det gerne videre til en, der trænger til at sænke tempoet.
          </p>
          <a
            href="/samling.pdf"
            target="_blank"
            rel="noopener"
            className="btn-base inline-flex"
          >
            Hent samlingen (PDF) →
          </a>
        </div>

        <div className="py-8 md:py-12 md:pl-11">
          <img
            src={brevMotif}
            alt=""
            aria-hidden="true"
            width={512}
            height={512}
            loading="lazy"
            className="mb-5 w-[72px] md:w-[88px] h-auto opacity-80"
          />
          <SubscribeSection subscriberCount={subscriberCount} />
        </div>
      </section>


      {/* Blød støttenote før footeren */}
      <SupportNote />
    </div>
  );
}
